import { useEffect, useRef } from 'react';

// Lazily loads body-pix (already a dependency) and produces a person-segmentation
// mask used to confine the tan tint to the person (no more tinting skin-coloured
// backgrounds). Segmentation runs on every frame the caller asks for; the only
// guard is "don't start a new pass while one is still running" (the model can be
// slower than the feed). There is intentionally no mask-level smoothing — if the
// effect looks jittery, lower the whole feed's frame rate at the render loop.
//
// Usage in a render loop:
//   bodyMask.updateFromVideo(video);            // runs segmentation (non-blocking)
//   const m = bodyMask.getMask();               // { data:Uint8Array, width, height } | null
//   const personAlpha = m && m.width === canvas.width ? m.data[i >> 2] : 1;
//
// `data` is 0/1 per pixel at the video's native resolution, so it indexes 1:1
// with a canvas sized to the video.
export default function useBodyPixMask({ ready } = {}) {
  const netRef = useRef(null);
  const enabledRef = useRef(false);
  const maskRef = useRef(null); // Uint8Array (0/1)
  const sizeRef = useRef({ width: 0, height: 0 });
  const busyRef = useRef(false);

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;

    (async () => {
      try {
        await import('@tensorflow/tfjs'); // register the webgl backend first
        const bodyPix = await import('@tensorflow-models/body-pix');
        const net = await bodyPix.load({
          architecture: 'MobileNetV1',
          outputStride: 16,
          multiplier: 0.5,
          quantBytes: 2,
        });
        if (cancelled) return;
        netRef.current = net;
        enabledRef.current = true;
      } catch (err) {
        // No segmentation available -> the filter falls back to skin-only tint.
        console.warn('useBodyPixMask: body-pix unavailable; background suppression off', err);
        enabledRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
      netRef.current = null;
      enabledRef.current = false;
      maskRef.current = null;
      sizeRef.current = { width: 0, height: 0 };
    };
  }, [ready]);

  // Runs every call; skips only if a previous pass hasn't resolved yet, so the
  // mask is always the most recent completed segmentation.
  const updateFromVideo = (video) => {
    if (!enabledRef.current || !netRef.current || !video || !video.videoWidth || busyRef.current) return;
    busyRef.current = true;
    netRef.current
      .segmentPerson(video, { internalResolution: 'low', segmentationThreshold: 0.7, maxDetections: 1 })
      .then((seg) => {
        maskRef.current = seg.data;
        sizeRef.current = { width: seg.width, height: seg.height };
      })
      .catch(() => {})
      .finally(() => {
        busyRef.current = false;
      });
  };

  const getMask = () => {
    if (!enabledRef.current || !maskRef.current) return null;
    return { data: maskRef.current, width: sizeRef.current.width, height: sizeRef.current.height };
  };

  return { updateFromVideo, getMask, isEnabled: () => enabledRef.current };
}
